import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import * as Rx from 'rxjs';
import * as Op from 'rxjs/operators';
import { useObservable } from 'react-use';
import { red } from '@material-ui/core/colors';
import {
  Switch,
  ThemeProvider,
  withStyles,
  Popover,
  Divider,
  Box,
} from '@material-ui/core';

import { add } from 'utils/index';
import Icon from 'components/Icon';
import {
  getConnectionHealth,
  getConnectionStatus,
  getIsForcedOfflineMode,
  getIsOnlineAndLoggedIn,
} from 'reducers/connectivity/connection';
import {
  setForcedOfflineStatus,
  syncPendingRequest,
} from 'actions/connectivity';
import { lightTheme } from 'utils/muiTheme';
import { addWindowFetchOverride } from 'utils/windowFetchOverrides';
import './Connectivity.scss';
import { fetchCampaignsAndCoupons } from 'actions/CampaignsDB';
import { campaignDataNotFetched } from 'reducers/CampaignsDB';
import { useBreakpoints } from 'utils/hooks/UI';

import ConnectivityInfoPopup from './ConnectivityInfoPopup';

const networkUpdates$ = new Rx.BehaviorSubject(0);
const networkStatus$ = new Rx.BehaviorSubject(0);
networkUpdates$
  .pipe(
    Op.scan(add, 0),
    Op.bufferTime(500),
    Op.filter(a => 0 < a.length),
    Op.map(arr => Math.max(...arr)),
  )
  .subscribe(networkStatus$);

const countRequestsOverride = orig => (...params) => {
  networkUpdates$.next(1);
  const ret = orig.apply(window, params);
  ret
    .catch(() => {
      // empty catch
    })
    .finally(() => {
      networkUpdates$.next(-1);
    });
  return ret;
};

addWindowFetchOverride('countRequestsOverride', countRequestsOverride);

let isForcedOffline = false;
const forceOfflineOverride = orig => (...params) => {
  if (!isForcedOffline) return orig.apply(window, params);
  const url = params?.[0];
  if (url.match(/((\/health$)|(localhost.erply))/))
    return orig.apply(window, params);
  return Promise.reject(new Error('Forced offline mode is enabled'));
};

addWindowFetchOverride('forceOfflineOverride', forceOfflineOverride);

const useForceOffline = () => {
  const isForcedOfflineMode = useSelector(getIsForcedOfflineMode);
  useEffect(() => {
    isForcedOffline = isForcedOfflineMode;
  }, [isForcedOfflineMode]);
};

const useSyncPendingRequests = () => {
  const dispatch = useDispatch();
  const isOnlineAndLoggedIn = useSelector(getIsOnlineAndLoggedIn);

  useEffect(() => {
    if (isOnlineAndLoggedIn) {
      dispatch(syncPendingRequest());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnlineAndLoggedIn]);
};

const useCoupons = () => {
  const dispatch = useDispatch();
  const isOnlineAndLoggedIn = useSelector(getIsOnlineAndLoggedIn);
  const couponsEmpty = useSelector(campaignDataNotFetched);

  useEffect(() => {
    if (couponsEmpty && isOnlineAndLoggedIn) {
      dispatch(fetchCampaignsAndCoupons());
    }
  }, [isOnlineAndLoggedIn, couponsEmpty, dispatch]);
};

const RedSwitch = withStyles({
  switchBase: {
    color: red[300],
    '&$checked': {
      color: red[500],
    },
    '&$checked + $track': {
      backgroundColor: red[500],
    },
  },
  checked: {},
  track: {},
})(Switch);

const HeaderConnectivity = () => {
  const dispatch = useDispatch();

  const connectionStatus = useSelector(getConnectionStatus);
  const connectionHealth = useSelector(getConnectionHealth);
  const isForcedOfflineMode = useSelector(getIsForcedOfflineMode);
  const activity = useObservable(networkStatus$, 0);
  const isMobileView = !useBreakpoints().mdplus;
  const popoverPaperProps = {
    style: {
      maxWidth: '300px',
      minWidth: '300px',
      maxHeight: `calc(${isMobileView ? 80 : 100}vh - 20px)`,
      overflowY: 'auto',
    },
  };

  const { t } = useTranslation(['header', 'alerts']);

  const connectionActivity = useMemo(
    () =>
      new Array(activity)
        .fill(0)
        .map((z, i) => (
          <i
            key={String(i)}
            className="erply-header__connection-activity arrow_up-down_alt"
          />
        )),
    [activity],
  );

  const onChange = useCallback(
    () => dispatch(setForcedOfflineStatus(!isForcedOfflineMode)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isForcedOfflineMode],
  );

  const [anchorEl, setAnchorEl] = useState(null);

  useForceOffline();
  useSyncPendingRequests();
  useCoupons();

  const className = `erply-header__connection ${!connectionHealth &&
    'erply-header__offline-mode'} erply-header__icon erply-header__connection--${connectionStatus}`;
  return (
    <ThemeProvider theme={lightTheme}>
      <Box className={className} title={t('titles.connectivity')}>
        {connectionActivity}
        <Icon action={e => setAnchorEl(e.currentTarget)} name="erply_wifi" />
      </Box>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        PaperProps={popoverPaperProps}
      >
        <Box style={{ padding: '16px 32px' }}>
          <strong>
            {`${t('connectionStatuses.mode', { ns: 'alerts' })} ${
              connectionHealth
                ? 'Online'
                : `${isForcedOfflineMode && ' Forced '}Offline`
            }`}
          </strong>
        </Box>
        <Divider />
        {connectionStatus === 'error' || isForcedOfflineMode ? (
          <Trans
            i18nKey="lostConnection.description"
            ns="alerts"
            values={{ type: isForcedOfflineMode ? 'forced' : '' }}
            components={{ title: <strong />, p: <p /> }}
          />
        ) : (
          <Box style={{ padding: '16px 32px' }}>
            <strong>
              {t('connectionStatuses.status', { ns: 'alerts' })}
              <span className={`connection-status-color ${connectionStatus}`}>
                {connectionStatus}
              </span>
            </strong>
          </Box>
        )}
        <Divider />
        <Box onClick={e => e.stopPropagation()} className="force-offline">
          <strong style={{ padding: '16px 32px' }}>Force Offline</strong>
          <RedSwitch checked={isForcedOfflineMode} onChange={onChange} />
        </Box>
        <Divider />

        <ConnectivityInfoPopup />
      </Popover>
    </ThemeProvider>
  );
};

export default HeaderConnectivity;
